import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive, Pagination } from "@/pages/Senders";
import { StatusBadge } from "@/components/StatusBadge";
import { Play, Pause, Stop, ArrowLeft, Timer, Pencil } from "@phosphor-icons/react";
import { estimateCampaignEta } from "@/lib/campaignEta";
import ContentPreviewModal from "@/components/ContentPreviewModal";
import { useCampaignElapsed, formatElapsed, formatHuman } from "@/lib/campaignTimer";

export default function CampaignDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [csenders, setCsenders] = useState([]);
  const [csendersTotal, setCsendersTotal] = useState(0);
  const [queue, setQueue] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [tab, setTab] = useState("overview");
  // Independent pagination per tab.
  const [pageSize, setPageSize] = useState(100);
  const [sendersPage, setSendersPage] = useState(0);
  const [queuePage, setQueuePage] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [previewRow, setPreviewRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const { connected, send } = useLiveSocket((msg) => {
    if (msg.type === "campaign" && msg.id === id) {
      setData(msg.data);
    }
  });

  // Subscribe to campaign updates via WS
  useEffect(() => {
    if (connected && id) send({ type: "subscribe_campaign", id });
    return () => { if (connected && id) send({ type: "unsubscribe_campaign", id }); };
  }, [connected, id, send]);

  const load = async () => {
    try {
      const r = await api.get(`/campaigns/${id}`);
      setData(r.data);
      const l = await api.get(`/logs`, { params: { campaign_id: id, limit: pageSize, skip: logsPage * pageSize } });
      setLogs(l.data.logs);
      setLogsTotal(l.data.total || 0);
      const cs = await api.get(`/campaigns/${id}/campaign-senders`, { params: { limit: pageSize, skip: sendersPage * pageSize } });
      setCsenders(cs.data.campaign_senders);
      setCsendersTotal(cs.data.total || 0);
      const q = await api.get(`/campaigns/${id}/queue`, { params: { limit: pageSize, skip: queuePage * pageSize } });
      setQueue(q.data.items);
      setQueueTotal(q.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [id, pageSize, sendersPage, queuePage, logsPage]);

  const ctrl = async (action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(action);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = () => {
    setEditForm({
      worker_count: c.worker_count ?? 10,
      batch_size: c.batch_size ?? 250,
      pause_minimum_seconds: c.pause_minimum_seconds ?? 1800,
      pause_maximum_seconds: c.pause_maximum_seconds ?? 3600,
      emails_per_mailbox: c.emails_per_mailbox ?? 10,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(editForm).map(([k, v]) => [k, Number(v)])
      );
      const r = await api.patch(`/campaigns/${id}`, payload);
      toast.success("Campaign updated — resume to continue where it paused");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (!data) return <div className="p-8 font-mono text-[#71717A]">[ LOADING... ]</div>;
  const c = data.campaign;
  const totalRecipients = c.total ?? data.total ?? 0;
  const pct = totalRecipients > 0 ? Math.min(100, (c.total_processed / totalRecipients) * 100) : 0;

  const resumeCountdown = c.scheduled_resume_at ? Math.max(0, Math.floor((new Date(c.scheduled_resume_at).getTime() - Date.now()) / 1000)) : 0;

  return (
    <div className="p-8">
      <Link to="/campaigns" className="micro flex items-center gap-1 text-[#A1A1AA] hover:text-white mb-4" data-testid="back-btn">
        <ArrowLeft size={12} weight="bold" /> Campaigns
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Campaign · {c.id.slice(0, 8)}</div>
          <div className="flex items-baseline gap-3">
            <h1 className="font-heading text-4xl font-bold tracking-tighter">{c.name}</h1>
            <div className={c.status === "running" ? "live-glow" : ""}>
              <StatusBadge status={c.status} />
            </div>
          </div>
          {c.description && <div className="text-sm text-[#A1A1AA] mt-1">{c.description}</div>}
        </div>
        <div className="flex gap-2">
          {c.status === "draft" && <BtnPrimary onClick={() => ctrl("start")} testid="cd-start"><Play size={12} weight="fill" /> Start</BtnPrimary>}
          {c.status === "running" && <BtnSecondary onClick={() => ctrl("pause")} testid="cd-pause"><Pause size={12} weight="fill" /> Pause</BtnSecondary>}
          {(c.status === "paused" || c.status === "draft") && <BtnSecondary onClick={openEdit} testid="cd-edit"><Pencil size={12} weight="bold" /> Edit</BtnSecondary>}
          {c.status === "paused" && <BtnPrimary onClick={() => ctrl("resume")} testid="cd-resume"><Play size={12} weight="fill" /> Resume</BtnPrimary>}
          {(c.status === "running" || c.status === "paused") && <BtnDestructive onClick={() => ctrl("stop")} testid="cd-stop"><Stop size={12} weight="fill" /> Stop</BtnDestructive>}
        </div>
      </div>

      <div className="thin-bar mb-2">
        <div style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-xs font-mono text-[#A1A1AA] mb-6">
        <span>{c.total_processed} / {totalRecipients} processed</span>
        <span>{pct.toFixed(1)}%</span>
      </div>

      <ElapsedTimerCard campaign={c} />

      {c.scheduled_resume_at && resumeCountdown > 0 && (
        <div className="border border-[#FF9500]/40 bg-[#FF9500]/10 p-3 mb-6 flex justify-between items-center" data-testid="pause-info">
          <div className="text-xs font-mono">
            <span className="text-[#FF9500]">BATCH PAUSED</span> · Resuming in {Math.floor(resumeCountdown / 60)}m {resumeCountdown % 60}s
          </div>
          <div className="text-xs font-mono text-[#71717A]">
            Resume at {new Date(c.scheduled_resume_at).toLocaleTimeString()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 border border-[#2A2A2A] mb-4">
        <Stat label="Recipients" v={totalRecipients} />
        <Stat label="Processed" v={c.total_processed} />
        <Stat label="Accepted" v={c.total_accepted} tone="success" />
        <Stat label="Failed" v={c.total_failed} tone="error" />
        <Stat label="Proxy fails" v={c.total_proxy_failed} tone="warning" />
        <Stat label="Remaining" v={data.remaining} tone="brand" />
        <Stat label="Batch #" v={c.current_batch_number || 1} />
        <Stat label="Batch size" v={c.batch_size} />
        <Stat label="Workers" v={c.worker_count} />
        <Stat label="Active workers" v={c.active_workers || 0} tone="brand" />
        <Stat label="Emails/mailbox" v={c.emails_per_mailbox} />
        <Stat label="Cycle" v={c.current_sender_cycle} />
      </div>

      {(() => {
        const eta = estimateCampaignEta({ ...c, total: totalRecipients, remaining: data.remaining });
        const pct = eta.percent ?? 0;
        return (
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-4 mb-6" data-testid="cd-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="micro text-[#E1FF00]">Campaign progress</span>
                <span className="text-[11px] text-[#71717A] font-mono">
                  {c.total_processed} of {totalRecipients} recipients
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[11px] text-[#71717A] font-mono uppercase tracking-wider">ETA</span>
                <span className={`data-num text-xl font-bold ${eta.method === "measured" ? "text-[#00E5FF]" : "text-[#A1A1AA]"}`} data-testid="cd-eta">
                  {eta.human}
                </span>
                <span className="text-[10px] text-[#71717A] font-mono">
                  ({eta.method === "measured" ? "measured throughput" : "estimated · pre-start"})
                </span>
                <span className="data-num text-2xl font-bold text-[#E1FF00]">{pct}%</span>
              </div>
            </div>
            <div className="h-2 w-full bg-[#141414] overflow-hidden">
              <div className="h-full bg-[#E1FF00] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })()}

      <div className="flex border border-[#2A2A2A] w-fit mb-4">
        {[["overview", "Senders"], ["queue", "Queue"], ["logs", "Logs"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cd-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase font-mono tracking-wider ${tab === k ? "bg-[#E1FF00] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full">
              <thead>
                <tr>
                  <th className="num">Order</th>
                  <th>Sender</th>
                  <th>Proxy IP</th>
                  <th>Proxy status</th>
                  <th className="num">Processed</th>
                  <th className="num">Accepted</th>
                  <th className="num">Failed</th>
                  <th className="num">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {csenders.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-[#71717A] py-6 font-mono">[ no senders ]</td></tr>
                )}
                {csenders.map((cs) => (
                  <tr key={cs.id}>
                    <td className="num data-num text-[#71717A]">{cs.sender_order}</td>
                    <td className="font-mono text-xs">{cs.sender_email}</td>
                    <td className="font-mono text-xs">{cs.proxy_public_ip || "—"}</td>
                    <td><StatusBadge status={cs.proxy_status} /></td>
                    <td className="num data-num">{cs.processed_count}</td>
                    <td className="num data-num text-[#00FF66]">{cs.accepted_count}</td>
                    <td className="num data-num text-[#FF3B30]">{cs.failed_count}</td>
                    <td className="num data-num">{cs.remaining_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {csendersTotal > 0 && (
            <Pagination
              page={sendersPage}
              pageSize={pageSize}
              total={csendersTotal}
              onPageChange={setSendersPage}
              onPageSizeChange={(s) => { setPageSize(s); setSendersPage(0); setQueuePage(0); setLogsPage(0); }}
              sizeOptions={[100, 200, 250, 500]}
              testidPrefix="cd-senders-pg"
            />
          )}
        </>
      )}

      {tab === "queue" && (
        <>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Recipient</th>
                  <th>Sender</th>
                  <th>Batch</th>
                  <th>Cycle</th>
                  <th>Status</th>
                  <th>Subject</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {queue.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-[#71717A] py-6 font-mono">[ empty ]</td></tr>
                )}
                {queue.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => setPreviewRow(q.id)}
                    className="cursor-pointer hover:bg-[#0F0F0F]"
                    data-testid={`cd-queue-row-${q.recipient_order}`}
                  >
                    <td className="num data-num text-[#71717A]">{q.recipient_order}</td>
                    <td className="font-mono text-xs">{q.recipient_email}</td>
                    <td className="font-mono text-xs">{q.assigned_sender_email}</td>
                    <td className="data-num">B{q.batch_number}</td>
                    <td className="data-num">{q.sender_cycle_number}</td>
                    <td><StatusBadge status={q.status} /></td>
                    <td className="truncate max-w-xs">{q.final_subject}</td>
                    <td className="font-mono text-[10px] text-[#E1FF00] uppercase tracking-wider">View</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {queueTotal > 0 && (
            <Pagination
              page={queuePage}
              pageSize={pageSize}
              total={queueTotal}
              onPageChange={setQueuePage}
              onPageSizeChange={(s) => { setPageSize(s); setSendersPage(0); setQueuePage(0); setLogsPage(0); }}
              sizeOptions={[100, 200, 250, 500]}
              testidPrefix="cd-queue-pg"
            />
          )}
        </>
      )}

      {tab === "logs" && (
        <>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Sender</th>
                  <th>Recipient</th>
                  <th>Proxy IP</th>
                  <th>Status</th>
                  <th>Provider response</th>
                  <th>Error</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-[#71717A] py-6 font-mono">[ no logs ]</td></tr>
                )}
                {logs.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => l.queue_id && setPreviewRow(l.queue_id)}
                    className={`${l.queue_id ? "cursor-pointer hover:bg-[#0F0F0F]" : ""}`}
                    data-testid={`cd-logs-row-${l.id}`}
                  >
                    <td className="font-mono text-[10px] text-[#71717A]">{l.created_at?.slice(11, 19)}</td>
                    <td className="font-mono text-xs">{l.sender_email}</td>
                    <td className="font-mono text-xs">{l.recipient_email}</td>
                    <td className="font-mono text-xs">{l.proxy_public_ip}</td>
                    <td><StatusBadge status={l.status} /></td>
                    <td className="font-mono text-[11px] text-[#71717A] truncate max-w-xs">{l.provider_response}</td>
                    <td className="font-mono text-[11px] text-[#FF3B30] truncate max-w-xs">{l.error_message || l.proxy_error_message}</td>
                    <td className="font-mono text-[10px] text-[#E1FF00] uppercase tracking-wider">{l.queue_id ? "View" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {logsTotal > 0 && (
            <Pagination
              page={logsPage}
              pageSize={pageSize}
              total={logsTotal}
              onPageChange={setLogsPage}
              onPageSizeChange={(s) => { setPageSize(s); setSendersPage(0); setQueuePage(0); setLogsPage(0); }}
              sizeOptions={[100, 200, 250, 500]}
              testidPrefix="cd-logs-pg"
            />
          )}
        </>
      )}

      {previewRow && (
        <ContentPreviewModal campaignId={id} rowId={previewRow} onClose={() => setPreviewRow(null)} />
      )}

      {editOpen && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="cd-edit-modal">
          <div className="w-full max-w-md bg-[#111] border border-[#2A2A2A] p-6">
            <div className="font-heading text-2xl font-bold tracking-tight mb-1">Edit campaign</div>
            <p className="text-xs text-[#A1A1AA] font-mono mb-5">
              Changes apply on Resume. Progress, senders and content stay exactly the same — the campaign continues from where it paused.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <EditField label="Workers (1–50)" testid="cd-edit-workers"
                value={editForm.worker_count}
                onChange={(v) => setEditForm({ ...editForm, worker_count: v })} />
              <EditField label="Batch size" testid="cd-edit-batch"
                value={editForm.batch_size}
                onChange={(v) => setEditForm({ ...editForm, batch_size: v })} />
              <EditField label="Emails / mailbox" testid="cd-edit-epm"
                value={editForm.emails_per_mailbox}
                onChange={(v) => setEditForm({ ...editForm, emails_per_mailbox: v })} />
              <EditField label="Pause min (sec)" testid="cd-edit-pmin"
                value={editForm.pause_minimum_seconds}
                onChange={(v) => setEditForm({ ...editForm, pause_minimum_seconds: v })} />
              <EditField label="Pause max (sec)" testid="cd-edit-pmax"
                value={editForm.pause_maximum_seconds}
                onChange={(v) => setEditForm({ ...editForm, pause_maximum_seconds: v })} />
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <BtnSecondary onClick={() => setEditOpen(false)} testid="cd-edit-cancel">Cancel</BtnSecondary>
              <BtnPrimary onClick={saveEdit} testid="cd-edit-save">Save changes</BtnPrimary>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      <input type="number" min="0" value={value}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none font-mono" />
    </label>
  );
}

function ElapsedTimerCard({ campaign }) {
  const { seconds, running, endReason } = useCampaignElapsed(campaign);
  if (!campaign?.started_at) return null;
  const finalLabel = endReason === "completed" ? "Total time (completed)"
                   : endReason === "stopped" ? "Total time (stopped)"
                   : "Elapsed";
  const color = running ? "text-[#E1FF00]" : endReason === "completed" ? "text-[#00FF66]" : "text-[#A1A1AA]";
  return (
    <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-4 mb-4 flex items-center justify-between" data-testid="cd-timer">
      <div className="flex items-center gap-3">
        <Timer size={18} weight="bold" className={color} />
        <div>
          <div className="micro">{finalLabel}</div>
          <div className={`data-num text-2xl font-bold ${color} ${running ? "live-glow" : ""}`}>
            {formatElapsed(seconds)}
          </div>
        </div>
      </div>
      {!running && (
        <div className="text-[11px] font-mono text-[#71717A]">
          {formatHuman(seconds)}
        </div>
      )}
      {running && (
        <div className="text-[10px] font-mono text-[#71717A] uppercase tracking-wider">
          ticking · continues through pauses
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, tone }) {
  const c = tone === "success" ? "text-[#00FF66]" :
            tone === "error" ? "text-[#FF3B30]" :
            tone === "warning" ? "text-[#FF9500]" :
            tone === "brand" ? "text-[#E1FF00]" : "text-white";
  return (
    <div className="border-r border-b border-[#2A2A2A] last:border-r-0 p-3">
      <div className="micro mb-1">{label}</div>
      <div className={`data-num text-lg font-bold ${c}`}>{v ?? "—"}</div>
    </div>
  );
}
